import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { dataAccessBadgeColors, levelBadgeColors, type Position } from "@/mocks/positionsData";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import AddEditPositionModal from "./components/AddEditPositionModal";

export default function PositionsPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Position | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const sortedPositions = [...positions].sort((a, b) => a.level - b.level);

  // ─── Fetch positions ─────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi<any>("/settings/positions", undefined, { data: { items: [] } });
      const items = res?.data?.items || res?.items || (Array.isArray(res) ? res : []);
      setPositions(items);
    } catch (err: any) {
      showToast(err.message || "Failed to load positions", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const handleAdd = () => {
    setEditingPosition(null);
    setModalOpen(true);
  };

  const handleEdit = (pos: Position) => {
    setEditingPosition(pos);
    setModalOpen(true);
    setMenuOpen(null);
  };

  const handleDelete = (pos: Position) => {
    setDeleteConfirm(pos);
    setMenuOpen(null);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      await fetchApi(`/settings/positions/${deleteConfirm.id}`, { method: "DELETE" });
      showToast(`${deleteConfirm.name} deleted`, "success");
      await fetchPositions();
    } catch (err: any) {
      showToast(err.message || "Failed to delete position", "error");
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleViewUsers = (pos: Position) => {
    setMenuOpen(null);
    navigate(`/settings/users?position=${encodeURIComponent(pos.name)}`);
  };

  const handleSave = async (data: Omit<Position, "id" | "createdAt" | "userCount">, id?: string) => {
    try {
      if (id) {
        await fetchApi(`/settings/positions/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        });
        showToast("Position updated successfully", "success");
      } else {
        await fetchApi("/settings/positions", {
          method: "POST",
          body: JSON.stringify(data),
        });
        showToast("Position created successfully", "success");
      }
      setModalOpen(false);
      setEditingPosition(null);
      await fetchPositions();
    } catch (err: any) {
      showToast(err.message || "Failed to save position", "error");
    }
  };

  return (
    <CrmLayout title="Positions & Roles">
      <div className="p-5 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-gray-900">Positions &amp; Roles</h1>
            <p className="text-sm text-gray-500">
              Define positions in your company hierarchy and set permissions for each position.
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-sm"
          >
            <i className="ri-add-line w-4 h-4 flex items-center justify-center" />
            Add Position
          </button>
        </div>

        {/* Info banner */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <p className="text-sm text-gray-700">
            Set up positions from top to bottom. Each position reports to the one above it. Users assigned to a position inherit its permissions and data access rules.
          </p>
        </div>

        {/* Table / List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              Loading positions...
            </div>
          ) : positions.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              No positions created yet. Click &quot;Add Position&quot; to set up your hierarchy.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sortedPositions.map((pos) => {
                const reportToPos = positions.find((p) => p.id === pos.reportsTo);
                const activePermCount = Object.values(pos.permissions || {}).filter(Boolean).length;

                return (
                  <div
                    key={pos.id}
                    className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gray-50/70 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white text-sm flex-shrink-0 shadow-xs"
                        style={{ backgroundColor: pos.color || "#6366f1" }}
                      >
                        L{pos.level}
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 text-sm">{pos.name}</h3>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              levelBadgeColors[pos.level] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            Level {pos.level}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              dataAccessBadgeColors[pos.dataAccess] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {pos.dataAccess === "own_only"
                              ? "Own Only"
                              : pos.dataAccess === "team_only"
                              ? "Team Only"
                              : pos.dataAccess === "branch_only"
                              ? "Branch Only"
                              : "All Company"}
                          </span>
                        </div>

                        <p className="text-xs text-gray-500 mt-1">
                          Reports to:{" "}
                          <span className="font-medium text-gray-700">
                            {reportToPos ? reportToPos.name : "None (Top Level)"}
                          </span>{" "}
                          &bull; {activePermCount} permissions enabled &bull; {pos.userCount || 0} users assigned
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      <button
                        onClick={() => handleViewUsers(pos)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        View Users
                      </button>

                      <div className="relative">
                        <button
                          onClick={() => setMenuOpen(menuOpen === pos.id ? null : pos.id)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-more-2-fill" />
                        </button>

                        {menuOpen === pos.id && (
                          <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-xl border border-gray-200 shadow-lg py-1 z-20 animate-scale-in text-left">
                            <button
                              onClick={() => handleEdit(pos)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                            >
                              <i className="ri-edit-line text-sm" /> Edit Position
                            </button>
                            <button
                              onClick={() => handleDelete(pos)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal */}
        {modalOpen && (
          <AddEditPositionModal
            isOpen={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setEditingPosition(null);
            }}
            onSave={handleSave}
            editingPosition={editingPosition}
            allPositions={positions}
          />
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-sm p-6 text-center animate-scale-in">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 text-red-600">
                <i className="ri-delete-bin-line text-2xl" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Delete Position?</h3>
              <p className="text-xs text-gray-500 mt-2 mb-6">
                Are you sure you want to delete &quot;{deleteConfirm.name}&quot;? Users assigned to this position will need to be reassigned.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                >
                  {isDeleting ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}